import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BrokerOutreachCardDto } from "@/api/brokerOutreach";
import {
  buyerSubmissionsApi,
  type BrokerIntroEmailStateDto,
} from "@/api/buyers";
import {
  Button,
  EmptyState,
  Skeleton,
  Textarea,
  Timestamp,
  useToast,
} from "@/design-system/components/ui";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { cn } from "@/design-system/lib/utils";
import { useSendHistory } from "@/hooks/buyers/useSendHistory";
import { communicationKeys } from "@/pages/communications/hooks/useCommunications";
import { CollapsibleCard } from "../introCards/CollapsibleCard";
import { HeaderAction } from "../introCards/HeaderAction";
import { InfoHint } from "../introCards/InfoHint";
import { IntroCardPill } from "../introCards/IntroCardPill";
import {
  draftNewerThanSend,
  isDone,
  latestVersion,
  messageCardStatus,
  outreachCardStatus,
} from "../introCards/introCardStatus";
import { EditAndSendAgain, SentFold } from "../introCards/SentFold";
import { InlineQueryError } from "../queryStates";
import { BrokerIntroCopyModal, type CopiedBrokerEmail } from "./BrokerIntroCopyModal";
import { BrokerIntroSendModal } from "./BrokerIntroSendModal";
import { brokerOutreachCopy, sentMatchesDraft } from "./brokerOutreachCopy";
import { CardSentLine } from "./CardStatusLine";
import { ListingLinkLine } from "./ListingLinkLine";

/**
 * The broker-facing intro email — the step BEFORE the package.
 *
 * <p>🔴 <b>A SIBLING of the buyer intro, and the two must never blur.</b> That
 * one asks the BUYER whether a listing is worth taking to its broker; this asks
 * the BROKER whether they would like the buyer's package at all. Since D23 they
 * sit on two tabs (Buyer intro, Broker intro) — this is the Broker intro tab's
 * Email card — and every label here still names the RECIPIENT rather than the
 * artifact — "to the listing broker", "{agent} at {address}", "before any
 * submission package". A specialist must never be one mis-click from writing to
 * the wrong audience.</p>
 *
 * <p><b>Draft, read, correct, send</b> — the same shape as its sibling, for the
 * same reason: the server refuses a send that never generated, so the
 * assistant's words cannot reach an outside broker without a specialist having
 * had them on screen.</p>
 *
 * <p>🔴 <b>Two trailers are shown and neither can be edited.</b> The
 * specialist's sign-off and Phoenix's regulated role statement are appended by
 * the transport, are never part of the editable body or the template, and go
 * out with both send paths. They are rendered as TEXT: a control that looked
 * editable and silently discarded a keystroke would be worse than none.</p>
 *
 * <p>🔴 <b>Sending this moves NOTHING about the buyer.</b> Not the buyer's
 * contact clocks — an email ABOUT somebody sent to a third party must never
 * make them look contacted — and not <c>PackageSentAtUtc</c>, which the
 * entitlement engine counts. The card says so before either button is
 * pressed.</p>
 *
 * <p>🔴 <b>Two send paths (D26 / R4): SEND FROM PHOENIX, or COPY FOR MY OWN
 * EMAIL then RECORD SEND.</b> Copying writes nothing; RECORD SEND records the
 * exact subject and text the copy put on the clipboard — the send version, the
 * sent columns and one communications entry on the buyer, the submission and
 * the listing. Between the two the card says "Copied — click RECORD SEND once
 * you've sent it."</p>
 *
 * <p><b>The Broker intro tab's Email card, collapsible (D2).</b> The tab owns
 * whether it is open. Its one pill reads the outreach state's Email card — the
 * LATEST send (J2) — and falls back to this panel's own read plus the send
 * history when the outreach read failed (J6). The typed draft lives here, above
 * the hidden body, so a collapse keeps it and the collapsed header's SEND is
 * disabled by the same unsaved edit. Once sent, the message is folded and the
 * composer waits behind "Edit and send again" unless the draft is newer than
 * that send (J8).</p>
 */
export function BrokerIntroEmailPanel({
  submissionId,
  canWrite,
  open,
  onOpenChange,
  outreachCard,
  onActed,
}: {
  submissionId: string;
  /**
   * 🔴 The page's one write answer — `s.canEdit && hasPermission("Submissions.Manage")`.
   *
   * <p>REQUIRED and deliberately not defaulted, for the reason its sibling
   * records: a default of `true` reproduces the defect where every read-only
   * role saw Generate and Send and 403'd on click, and a default of `false`
   * would let a caller silently lose the affordances instead.</p>
   */
  canWrite: boolean;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** The outreach state's Email card — the status source (J3). Null while that
   *  read is loading or failed. */
  outreachCard: BrokerOutreachCardDto | null;
  /** Called after a send or RECORD SEND this tab performed succeeds (J6). */
  onActed?: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [sending, setSending] = useState(false);
  const [copying, setCopying] = useState(false);
  const [revealed, setRevealed] = useState(false);
  // What COPY put on the clipboard, held until RECORD SEND sends it back.
  // Local, deliberately: a copy is not a record, and nothing about it is the
  // server's to know until the consultant says it went.
  const [copied, setCopied] = useState<CopiedBrokerEmail | null>(null);

  const state = useQuery({
    queryKey: buyerSubmissionKeys.brokerIntro(submissionId),
    queryFn: () => buyerSubmissionsApi.getBrokerIntro(submissionId),
    enabled: Boolean(submissionId),
  });
  const history = useSendHistory(submissionId);

  // The typed draft, reseeded when a (re)draft lands — what keying the composer
  // on `generatedAtUtc` did when the text lived inside it.
  const savedBody = state.data?.bodyDraft ?? "";
  const seed = state.data?.generatedAtUtc ?? null;
  const [seededFrom, setSeededFrom] = useState(seed);
  const [draft, setDraft] = useState(savedBody);
  if (seed !== seededFrom) {
    setSeededFrom(seed);
    setDraft(savedBody);
  }

  const generate = useMutation({
    mutationFn: () => buyerSubmissionsApi.generateBrokerIntro(submissionId),
    onSuccess: (s) => {
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      toast.success(
        s.aiModel
          ? "Draft ready. Read it through before you send it."
          : "Draft ready — composed from the buyer's own profile. Read it through before you send it.",
      );
    },
    // The 422 the daily AI ceiling raises arrives here with the server's own
    // sentence, which names the ceiling. Never compose a second one.
    onError: (e: Error) => toast.error(e.message),
  });

  const recordSend = useMutation({
    mutationFn: (c: CopiedBrokerEmail) => buyerSubmissionsApi.recordBrokerIntroSend(submissionId, c),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      // One entry, on the buyer's, the submission's and the listing's feeds.
      void qc.invalidateQueries({ queryKey: communicationKeys.all });
      setCopied(null);
      setRevealed(false);
      toast.success("Recorded as sent from your own email.");
      onActed?.();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const latest = latestVersion(history.data?.brokerIntros);
  const status = outreachCard
    ? outreachCardStatus(outreachCard, copied != null)
    : messageCardStatus({
        generatedAtUtc: state.data?.generatedAtUtc ?? null,
        sentAtUtc: state.data?.sentAtUtc ?? null,
        latest,
        copied: copied != null,
      });

  const card = (body: ReactNode, extras?: { info?: ReactNode; action?: ReactNode; pill?: boolean }) => (
    <CollapsibleCard
      open={open}
      onOpenChange={onOpenChange}
      title="Broker intro email"
      info={extras?.info}
      recipient="To the listing broker"
      pill={extras?.pill ? <IntroCardPill status={status} /> : undefined}
      action={extras?.action}
      testId="broker-email-card"
    >
      {body}
    </CollapsibleCard>
  );

  if (state.isLoading) return card(<Skeleton className="h-40 w-full" />);
  // Never `return null`. A vanished section is the least recoverable dishonest
  // state: nothing is left on screen to retry from, and nothing says a broker
  // intro section should be there at all.
  if (state.isError) {
    return card(
      <InlineQueryError
        what="the broker intro email"
        onRetry={() => void state.refetch()}
        retrying={state.isFetching}
      />,
    );
  }
  if (!state.data) return null;

  const email = state.data;
  const drafted = Boolean(email.generatedAtUtc);
  const done = isDone(status);
  const sentAt = status.state === "sent" ? status.date : email.sentAtUtc;
  const empty = draft.trim().length === 0;
  // Trimmed both sides: the server trims on save, so an untrimmed comparison
  // would leave the panel permanently dirty after a save.
  const dirty = draft.trim() !== (email.bodyDraft ?? "").trim();
  // J8 — unsent work: a draft generated after the latest send, or a saved draft
  // that no longer says what went out (the DTO carries no save time; the drift
  // the snapshot already reports is that fact).
  const drifted =
    email.sentBody != null &&
    email.bodyDraft != null &&
    !sentMatchesDraft(email.sentBody, email.bodyDraft, email.roleStatement, email.listingLinkLead);
  const unsentDraft = drafted && (draftNewerThanSend([email.generatedAtUtc], sentAt) || drifted);
  // J8 — writers only: a reader keeps today's read-only view, with no control to reach.
  // The editor area hides by CLASS as well as the attribute: Tailwind's preflight
  // `[hidden]` rule loses to `.grid` (same specificity, emitted earlier).
  const editorHidden = canWrite && done && !revealed && !unsentDraft;

  const action = !canWrite ? null : status.state === "notDrafted" ? (
    <HeaderAction
      label={generate.isPending ? "Drafting…" : "Draft broker intro"}
      onClick={() => generate.mutate()}
      disabled={generate.isPending}
      testId="broker-intro-header-draft"
    />
  ) : status.state === "copied" ? (
    <HeaderAction
      label={recordSend.isPending ? "Recording…" : "Record send"}
      onClick={() => copied && recordSend.mutate(copied)}
      disabled={recordSend.isPending || !email.canRecordCopySend}
      reason={email.canRecordCopySend ? null : email.copySendBlockedReason}
      testId="broker-intro-header-record-send"
    />
  ) : status.state === "draftSaved" ? (
    <HeaderAction
      label="Send from Phoenix"
      onClick={() => setSending(true)}
      disabled={dirty || empty || !email.canSend}
      reason={dirty ? UNSAVED : email.canSend ? null : email.blockedReason}
      testId="broker-intro-header-send"
    />
  ) : null;

  return card(
    <section className="grid gap-space-4" data-testid="broker-intro-panel">
      {done && outreachCard ? <CardSentLine card={outreachCard} testId="broker-email-sent-line" /> : null}

      {done ? <SentSnapshot email={email} drifted={drifted} /> : null}

      {editorHidden && canWrite ? (
        <EditAndSendAgain onClick={() => setRevealed(true)} testId="broker-intro-edit-again" />
      ) : null}

      <div hidden={editorHidden} className={cn("grid gap-space-4", editorHidden && "hidden")} data-testid="broker-intro-editor">
        {!drafted ? (
          <NotDrafted
            canWrite={canWrite}
            onDraft={() => generate.mutate()}
            drafting={generate.isPending}
          />
        ) : canWrite ? (
          <BrokerIntroComposer
            submissionId={submissionId}
            email={email}
            draft={draft}
            onDraftChange={setDraft}
            dirty={dirty}
            empty={empty}
            onSend={() => setSending(true)}
            onCopy={() => setCopying(true)}
            onRedraft={() => generate.mutate()}
            redrafting={generate.isPending}
            copied={copied}
            onRecordSend={() => copied && recordSend.mutate(copied)}
            recording={recordSend.isPending}
          />
        ) : (
          <ReadOnlyDraft email={email} />
        )}
      </div>

      {sending ? (
        <BrokerIntroSendModal
          submissionId={submissionId}
          email={email}
          onSent={() => {
            setRevealed(false);
            onActed?.();
          }}
          onClose={() => setSending(false)}
        />
      ) : null}

      {copying ? (
        <BrokerIntroCopyModal
          submissionId={submissionId}
          email={email}
          onCopied={setCopied}
          onClose={() => setCopying(false)}
        />
      ) : null}
    </section>,
    { info: <InfoHint text={explainerText(email)} testId="broker-intro-info" />, action, pill: true },
  );
}

/** The sentence an unsaved edit shows — under the composer, and as the collapsed
 *  header's SEND tooltip (J5). */
const UNSAVED = "Save your changes before sending — the send uses the saved draft, not what is on screen.";

/**
 * Who it goes to, when, and what it does and does not say.
 *
 * <p>D6 — behind the ⓘ beside the card's title, word for word. The broker is
 * NAMED when we know them, because "the listing broker" is a
 * role and a specialist about to write to somebody wants the person. With no
 * broker on file the sentence degrades to the role rather than printing a
 * placeholder — an email addressed to "(unknown)" reads as a gap in the buyer's
 * file rather than a gap in the listing's.</p>
 */
function explainerText(email: BrokerIntroEmailStateDto): string {
  const to = email.agentName
    ? `to ${email.agentName}, the listing broker for `
    : "to the listing broker for ";
  return (
    `A short introduction of ${email.buyerName} ${to}${email.listingName}. It goes before ` +
    "any submission package, to ask whether the broker would like to receive " +
    "it. It describes the buyer only from their profile, and it mentions the " +
    "prequalification only when it covers the asking price."
  );
}

function NotDrafted({
  canWrite,
  onDraft,
  drafting,
}: {
  canWrite: boolean;
  onDraft: () => void;
  drafting: boolean;
}) {
  if (!canWrite) {
    return (
      <p className="text-small text-gray-mid m-0">
        No broker intro has been drafted yet. Only this submission&apos;s
        consultant can draft one.
      </p>
    );
  }

  return (
    <EmptyState
      title="No broker intro drafted yet"
      description={
        "Drafting is its own step, so you read the email before the broker does. " +
        "Nothing is sent until you send it."
      }
      className="py-space-5"
    >
      {/* A real disabled Button rather than the `action` slot, which has no
          disabled state — a double-click there would buy two AI generations. */}
      <div className="mt-space-4">
        <Button onClick={onDraft} disabled={drafting} data-testid="broker-intro-draft">
          {drafting ? "Drafting…" : "Draft broker intro"}
        </Button>
      </div>
    </EmptyState>
  );
}

/**
 * The draft, editable, with the locked trailers and the send controls beneath.
 *
 * <p>Local state seeds from the server body and `dirty` is measured against the
 * LIVE prop, so a successful save clears it when the refetch lands rather than
 * leaving the send buttons stuck.</p>
 */
function BrokerIntroComposer({
  submissionId,
  email,
  draft,
  onDraftChange,
  dirty,
  empty,
  onSend,
  onCopy,
  onRedraft,
  redrafting,
  copied,
  onRecordSend,
  recording,
}: {
  submissionId: string;
  email: BrokerIntroEmailStateDto;
  /** The typed draft — held by the panel so a collapse keeps it (D2). */
  draft: string;
  onDraftChange: (next: string) => void;
  dirty: boolean;
  empty: boolean;
  onSend: () => void;
  onCopy: () => void;
  onRedraft: () => void;
  redrafting: boolean;
  /** What COPY put on the clipboard, or null — RECORD SEND needs it. */
  copied: CopiedBrokerEmail | null;
  onRecordSend: () => void;
  recording: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const save = useMutation({
    mutationFn: () =>
      buyerSubmissionsApi.updateBrokerIntro(submissionId, { body: draft }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      toast.success("Draft saved.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const alreadySent = Boolean(email.sentAtUtc);

  return (
    <div className="grid gap-space-3">
      <p className="text-small text-gray-mid m-0">
        Read it through and change anything that is not right — it goes to the
        broker under your name.
      </p>

      {/* 🔴 WHERE IT IS GOING, ON THE CARD, NOT ONLY IN THE SEND DIALOG.
          The address lived in BrokerIntroSendModal and BrokerIntroCopyModal
          alone, so a specialist reading the message through had the buyer's
          name, the listing's name and the broker's name in front of them and
          no way to see the one thing that decides who receives it. The two
          null states are DIFFERENT and say so: no broker on file at all is a
          listing problem, a broker with no address is a contact problem, and
          the blocked reason below only ever names one of them. */}
      <label className="grid gap-space-2">
        <span className="text-small font-medium">To</span>
        <p
          className="text-small text-onyx m-0 rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
          data-testid="broker-intro-to"
        >
          {email.agentName ? (
            <>
              {email.agentName}
              {email.agentEmail ? (
                <span className="text-gray-mid"> · {email.agentEmail}</span>
              ) : (
                <span className="text-gray-mid">
                  {" "}
                  · no email address on file
                </span>
              )}
            </>
          ) : (
            <span className="text-gray-mid">
              No listing broker on file for this listing
            </span>
          )}
        </p>
      </label>

      <label className="grid gap-space-2">
        <span className="text-small font-medium">Subject</span>
        {/* Composed server-side and not editable — there is nothing to keep, so
            there is nothing to edit. Shown because the specialist should see
            the whole message, not most of it. */}
        <p className="text-small text-onyx m-0 rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
          {email.subject}
        </p>
      </label>

      <label className="grid gap-space-2">
        <span className="text-small font-medium">Message</span>
        <Textarea
          rows={14}
          value={draft}
          data-testid="broker-intro-body"
          aria-label="Broker intro email message"
          onChange={(e) => onDraftChange(e.target.value)}
        />
      </label>

      <LockedTrailers email={email} />

      {/* Server-resolved and rendered whenever it exists. The six reasons are
          distinct on purpose: a specialist reading "not included" with no cause
          cannot tell a fixable listing from an unfixable buyer. */}
      {email.capacityNote ? (
        <p
          className="text-small text-gray-mid m-0"
          data-testid="broker-intro-capacity-note"
        >
          {email.capacityNote}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-space-3">
        <Button
          variant="secondary"
          onClick={() => save.mutate()}
          disabled={!dirty || empty || save.isPending}
        >
          {save.isPending ? "Saving…" : "Save draft"}
        </Button>
        <span className="text-caption text-gray-mid">
          {email.generatedAtUtc ? (
            <>
              Drafted <Timestamp value={email.generatedAtUtc} />
              {email.aiModel
                ? " by the assistant."
                : " without the assistant — composed straight from the buyer's profile."}
            </>
          ) : null}
        </span>
      </div>

      {/* 🔴 The send uses the SAVED draft. Without this the specialist edits a
          sentence, sends, and the broker receives the version they just fixed. */}
      {dirty ? (
        <p
          role="status"
          className="text-small text-status-warning m-0"
          data-testid="broker-intro-unsaved"
        >
          {UNSAVED}
        </p>
      ) : null}

      {/* Server-resolved, and the only reason rendered. `canSend` and this
          sentence come from the same rule the endpoint refuses on. */}
      {!email.canSend && email.blockedReason ? (
        <p
          className="text-small text-status-warning m-0"
          data-testid="broker-intro-blocked"
        >
          {email.blockedReason}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-space-3">
        <Button
          onClick={onSend}
          disabled={dirty || empty || !email.canSend}
          data-testid="broker-intro-send"
        >
          {alreadySent ? "Send again from Phoenix" : "Send from Phoenix"}
        </Button>
        {/*
          NOT gated on `canSend`. A broker with no address on file blocks the
          Phoenix path only — the server's own refusal says "or take the copy
          and send it from your own email", and disabling this button would
          make that sentence false.
        */}
        <Button
          variant="secondary"
          onClick={onCopy}
          disabled={dirty || empty}
          data-testid="broker-intro-copy"
        >
          Copy for my own email
        </Button>
        {/* R4: the record follows the SEND, so it is its own click — enabled
            only once something was copied, and refused server-side only when
            the listing has no broker at all (`canRecordCopySend`). */}
        <Button
          variant="secondary"
          onClick={onRecordSend}
          disabled={!copied || recording || !email.canRecordCopySend}
          data-testid="broker-intro-record-send"
        >
          {recording ? "Recording…" : "Record send"}
        </Button>
        <Button variant="text" onClick={onRedraft} disabled={redrafting}>
          {redrafting ? "Redrafting…" : "Redraft"}
        </Button>
      </div>

      {copied ? (
        <p role="status" className="text-small text-gray-mid m-0" data-testid="broker-intro-copied">
          {brokerOutreachCopy.copiedRecordSend}
        </p>
      ) : null}

      <p className="text-caption text-gray-mid m-0">
        Sending — or recording a send from your own email — records this broker
        intro and nothing else: it does not change the submission&apos;s status,
        it does not send the package, and it does not mark the buyer as
        contacted.
      </p>
    </div>
  );
}

/**
 * 🔴 What the transport appends, shown beneath the body it is appended to.
 *
 * <p>Both are LOCKED and rendered as text. The role statement is a regulated
 * claim about Phoenix's role that nobody may edit; the signature is resolved
 * from the acting user's own profile, so the place to change it is the profile
 * and a field here would silently discard a keystroke.</p>
 *
 * <p>An absent signature renders a SENTENCE rather than nothing, because its
 * absence is actionable: the specialist can add one and re-send. An absent role
 * statement is impossible — the server sends a constant.</p>
 */
function LockedTrailers({ email }: { email: BrokerIntroEmailStateDto }) {
  return (
    <div className="grid gap-space-3" data-testid="broker-intro-trailers">
      <ListingLinkLine line={email.listingLinkLine ?? null} testId="broker-intro-listing-link" />

      <div className="grid gap-space-2">
        <span className="text-caption text-gray-mid uppercase tracking-wider">
          Your sign-off, added automatically
        </span>
        {email.signatureBlock ? (
          <div className="rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
            <p
              className="text-small text-gray-mid whitespace-pre-wrap m-0"
              data-testid="broker-intro-signature"
            >
              {email.signatureBlock}
            </p>
          </div>
        ) : (
          <p className="text-small text-gray-mid m-0" data-testid="broker-intro-no-signature">
            You have no signature on file, so the message goes out without one.
            Add one on your profile.
          </p>
        )}
      </div>

      <div className="grid gap-space-2">
        <span className="text-caption text-gray-mid uppercase tracking-wider">
          Phoenix always adds this, and it cannot be edited
        </span>
        <div className="rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
          <p
            className="text-small text-gray-mid whitespace-pre-wrap m-0"
            data-testid="broker-intro-role-statement"
          >
            {email.roleStatement}
          </p>
        </div>
      </div>
    </div>
  );
}

/** What a colleague without the write sees: the draft, read-only. Seeing what a
 *  broker was told is not a write. */
function ReadOnlyDraft({ email }: { email: BrokerIntroEmailStateDto }) {
  return (
    <div className="grid gap-space-2">
      <span className="text-small font-medium">Current draft</span>
      <BodyBlock body={email.bodyDraft} />
      <p className="text-caption text-gray-mid m-0">
        Only this submission&apos;s consultant can edit or send it.
      </p>
    </div>
  );
}

/**
 * What went out — folded to three lines (D7).
 *
 * <p>Deliberately says "the latest version sent" rather than dating the
 * snapshot: the server stamps the send clock forward-only, so the column's
 * timestamp is the FIRST send while the snapshot is the newest body. The date
 * the card shows is the latest send's (J2), on the line above.</p>
 */
function SentSnapshot({ email, drifted }: { email: BrokerIntroEmailStateDto; drifted: boolean }) {
  const drift = drifted ? (
    <p className="text-small text-status-info m-0" data-testid="broker-intro-drift">
      The draft below has been edited since this went out.
    </p>
  ) : null;

  if (email.sentBody) {
    return (
      <SentFold body={email.sentBody} testId="broker-intro-sent">
        {drift}
      </SentFold>
    );
  }
  return (
    <div className="grid gap-space-2" data-testid="broker-intro-sent">
      <span className="text-small font-medium">What went out</span>
      <p className="text-small text-gray-mid m-0">
        This broker intro was recorded as sent, but no copy of the message was
        kept.
      </p>
      {drift}
    </div>
  );
}

function BodyBlock({ body }: { body: string | null }) {
  if (!body) {
    return <p className="text-small text-gray-mid m-0">Nothing drafted.</p>;
  }
  return (
    <div className="max-h-80 overflow-y-auto rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
      <p className="text-small text-onyx whitespace-pre-wrap m-0">{body}</p>
    </div>
  );
}
